// src/lib/opportunity/formatPaymentMilestoneEval.ts

import type { EvaluatedFormatCalcLine } from "./formatCalcEval.js";
import {
  PAYMENT_LINE_ITEM_BASE_REF,
  type FormatPaymentMilestoneDef,
} from "./formatPaymentMilestoneTypes.js";

export type EvaluatedPaymentMilestone = {
  label: string;
  subtext: string | null;
  amount: number;
  milestone_role: FormatPaymentMilestoneDef["milestone_role"];
};

export type PaymentLineAmountMap = Record<string, number>;

function roundUsd(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

export function buildPaymentLineAmountMap(args: {
  baseAmount: number;
  evaluatedCalcLines: EvaluatedFormatCalcLine[];
}): PaymentLineAmountMap {
  const map: PaymentLineAmountMap = {
    [PAYMENT_LINE_ITEM_BASE_REF]: roundUsd(args.baseAmount),
  };
  for (const line of args.evaluatedCalcLines) {
    map[line.id] = roundUsd(line.amount);
  }
  return map;
}

/**
 * Allocates milestone dollar amounts for the PDF payment schedule.
 * Deposit and balance use snapshot totals; custom lines use manager rules.
 */
export function evaluatePaymentMilestones(args: {
  milestones: FormatPaymentMilestoneDef[];
  depositAmount: number;
  totalSalePrice: number;
  remainingBalance: number;
  lineAmounts?: PaymentLineAmountMap;
}): EvaluatedPaymentMilestone[] {
  const { milestones, depositAmount, totalSalePrice, remainingBalance, lineAmounts = {} } = args;
  const sorted = [...milestones].sort((a, b) => a.sort_order - b.sort_order);
  const customs = sorted.filter((m) => m.milestone_role === "custom");
  const deposit = sorted.find((m) => m.milestone_role === "deposit");
  const balance = sorted.find((m) => m.milestone_role === "balance");

  const rows: EvaluatedPaymentMilestone[] = [];
  if (deposit) {
    rows.push({
      label: deposit.label,
      subtext: deposit.subtext,
      amount: roundUsd(depositAmount),
      milestone_role: "deposit",
    });
  }

  let pool = Math.max(0, totalSalePrice - depositAmount);
  for (const line of customs) {
    let amount = 0;
    if (line.amount_mode === "line_item_amount" && line.line_item_ref) {
      amount = roundUsd(lineAmounts[line.line_item_ref] ?? 0);
      pool = Math.max(0, pool - amount);
    } else {
      const pct = line.default_amount ?? 0;
      if (line.amount_mode === "percent_of_remaining") {
        amount = roundUsd((pool * pct) / 100);
        pool = Math.max(0, pool - amount);
      } else if (line.amount_mode === "percent_of_total") {
        amount = roundUsd((totalSalePrice * pct) / 100);
        pool = Math.max(0, pool - amount);
      } else if (line.amount_mode === "fixed") {
        amount = roundUsd(pct);
        pool = Math.max(0, pool - amount);
      }
    }
    rows.push({
      label: line.label,
      subtext: line.subtext,
      amount,
      milestone_role: "custom",
    });
  }

  if (balance) {
    const balanceAmount =
      customs.length > 0 ? roundUsd(pool) : roundUsd(remainingBalance);
    rows.push({
      label: balance.label,
      subtext: balance.subtext,
      amount: balanceAmount,
      milestone_role: "balance",
    });
  }

  return rows;
}
